import React, { useCallback } from 'react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { LanguagePack, loadLanguagePack } from '@americanexpress/one-app-ducks';
import { OnVisible } from '@americanexpress/data-manager-utils';
import { IntlProvider } from 'react-intl';
import type { LoadModuleData } from 'holocron';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import {
  isLoggedInThunk, membershipDomainApiThunk, navigationLinksThunk, personaApiThunk,
} from '../thunks/apiThunks';
import { useNavigationLinksHook, useMembershipApi } from '../apiHooks/apiHooks';
import { MODULE_NAME } from '../constants/commonConstants';
import styles from './TravelHeader.scss';
import dlsStyles from '../styles/dls.css';
import partialRenderOnly from '../selectors/partialRenderOnly';
import Desktop from './Desktop/Desktop';
import Mobile from './Mobile/Mobile';
import { init } from '../scripts/navScript';
import { subnavScriptURLSelector } from '../selectors/scriptSelector';

export type TravelHeaderModuleProps = {
  locale: string
  languageData: LanguagePack;
}
const TravelHeader = ({
  languageData,
  locale,
}: TravelHeaderModuleProps) => {
  const {
    isLoading: isMembershipLoading,
  } = useMembershipApi();

  const {
    isLoading: navigationLinksLoading,
  } = useNavigationLinksHook();

  const isPartialOnly = useSelector(partialRenderOnly);
  const scriptUrl = useSelector(subnavScriptURLSelector);

  const wrapperAddedToDOM = useCallback((node) => {
    if (!isPartialOnly) {
      if (node !== null) {
        init();
      } else {
        // no op
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- no deps
  }, []);

  if (Object.entries(languageData).length === 0 || isMembershipLoading || navigationLinksLoading) {
    return null;
  }

  return (
    <IntlProvider locale={locale} messages={languageData}>
      { isPartialOnly && <script type="module" src={scriptUrl} data-testid="partial-script" /> }
      <OnVisible
        analyticsInfo={{
          element: 'travel-header',
        }}
      >
        <div
          id="travel-header-wrapper"
          className={`${styles.wrapper} ${dlsStyles.travelheader}`}
          ref={wrapperAddedToDOM}
          data-dls-surface="foreground"
        >
          <Desktop />
          <Mobile />
        </div>
      </OnVisible>
    </IntlProvider>
  );
};

export const TestableTravelHeader = TravelHeader;

export const loadModuleData: LoadModuleData<TravelHeaderModuleProps> = async (
  { store: { getState, dispatch } }
) => {
  const state = getState();
  const activeLocale = getActiveLocaleSelector(state) || 'en-US';
  if (activeLocale === 'en-US') {
    await Promise.allSettled([dispatch(isLoggedInThunk)]);
    await Promise.allSettled([
      dispatch(navigationLinksThunk),
      dispatch(membershipDomainApiThunk),
      dispatch(loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' })),
    ]);
    if (partialRenderOnly(state)) {
      // TO:DO api onboarding to eag is in progress so this won't work atm
      await Promise.allSettled([dispatch(personaApiThunk)]);
    }
  }
};

TravelHeader.holocron = {
  name: MODULE_NAME,
  loadModuleData,
};

export default oneAppModuleWrapper(MODULE_NAME)(TravelHeader);
